import { Badge, Box, Stack, Tab, Tabs, useTheme } from "@mui/material";
import { mainColours } from "@qubit/autoparts";
import { useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import Andon from "~/features/andon/Andon";
import { selectIsAndonBoardFullScreen } from "~/features/andon/andon.slice";
import { selectEnableDevInformation } from "~/features/devTools/devTools.slice";
import { Status } from "~/features/status/Status";
import { TaskAssessment } from "~/features/status/TaskAssessment";

import TestingToolsView from "~/features/testingTools/TestingToolsView";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { useTabNavLocation } from "~/hooks/useTabNavLocation";

import { useWorkstationPolling } from "~/hooks/useWorkstationPolling";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import Configurations from "./Configurations";
import ServiceSupport from "./ServiceSupport";
import { UnifyView } from "./UnifyView";
import EventsTable from "./eventsMonitor/EventsTable";

type ServiceSupportViewProps = {
  viewTitle?: ViewNameTranslation;
};

const ServiceSupportView = (props: ServiceSupportViewProps) => {
  const { viewTitle } = props;
  const { t } = useTranslation();
  const { palette } = useTheme();

  useDevCheats({ showEventSimulator: true });
  useWorkstationPolling();

  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const shouldRenderTasksTab =
    usersFulfillmentCenter?.pickingConfigurations.includes("Autostore");
  const enableTestingToolsTab = useFlag().enableTestingTools;
  const trackedPageName = useMixpanelPageName();
  useNavbar({ viewTitle });

  const { an_andonFeaturesEnabled } = useAppSelector(selectServerConfig);
  const isAndonBoardFullScreen = useAppSelector(selectIsAndonBoardFullScreen);

  const shouldDisplayAndonTab = an_andonFeaturesEnabled;
  const shouldRenderConfigurationsTab = useAppSelector(
    selectEnableDevInformation
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const validTabs = [
    "service-support",
    "cube-analytics",
    ...(shouldRenderConfigurationsTab ? ["configurations"] : []),
    "status",
    ...(shouldRenderTasksTab ? ["tasks"] : []),
    ...(shouldDisplayAndonTab ? ["andon", "events"] : []),
    ...(enableTestingToolsTab ? ["testing-tools"] : [])
  ];

  const { tabIndex: tabIndexFromUrl, handleTabIndexChange } = useTabNavLocation(
    {
      defaultTab: "service-support",
      validTabs,
      key: "service-support-tab",
      subTabs: [
        {
          subTab: "configurations",
          subTabKey: "configurations-tab",
          subTabDefault: "client-configurations"
        },
        {
          subTab: "status",
          subTabKey: "status-tab",
          subTabDefault: "ports-status"
        },
        {
          subTab: "testing-tools",
          subTabKey: "testing-tools-tab",
          subTabDefault: "scanner-test"
        }
      ]
    }
  );

  /** Legacy: Andon events lived under ?service-support-tab=andon&andon-tab=events */
  const legacyAndonEventsDeepLink =
    shouldDisplayAndonTab &&
    tabIndexFromUrl === "andon" &&
    searchParams.get("andon-tab") === "events";

  // Legacy: Cube Analytics lived under ?service-support-tab=unify.
  // TODO(cube-analytics-legacy): back-compat only — redirects old `unify`
  // deep links to `cube-analytics`. Safe to remove (this const, the ternary
  // below, the useLayoutEffect, and ServiceSupportUnifyLegacyPathRoute in
  // serviceSupport.routes.tsx) once old `unify` links are no longer in use —
  // a release or two after the rename ships.
  const legacyUnifyDeepLink =
    searchParams.get("service-support-tab") === "unify";

  const tabIndex = legacyAndonEventsDeepLink
    ? "events"
    : legacyUnifyDeepLink
      ? "cube-analytics"
      : tabIndexFromUrl;

  useLayoutEffect(() => {
    if (!legacyAndonEventsDeepLink) return;
    const next = new URLSearchParams(searchParams);
    next.set("service-support-tab", "events");
    next.delete("andon-tab");
    setSearchParams(next, { replace: true });
  }, [legacyAndonEventsDeepLink, searchParams, setSearchParams]);

  // TODO(cube-analytics-legacy): remove together with legacyUnifyDeepLink above.
  useLayoutEffect(() => {
    if (!legacyUnifyDeepLink) return;
    const next = new URLSearchParams(searchParams);
    next.set("service-support-tab", "cube-analytics");
    setSearchParams(next, { replace: true });
  }, [legacyUnifyDeepLink, searchParams, setSearchParams]);

  const isCurrentTabAndonAndIsFullscreenMode =
    tabIndex === "andon" && isAndonBoardFullScreen;
  return (
    <Stack
      margin={3}
      marginTop={0}
      height="unset"
      sx={{
        overflowY: "unset"
      }}
    >
      <Box
        position="sticky"
        top="0px"
        bgcolor={palette.background.default}
        zIndex={1001}
      >
        {!isCurrentTabAndonAndIsFullscreenMode && (
          <Tabs
            value={tabIndex}
            onChange={(_event, newValue) => {
              if (newValue === "cube-analytics") {
                mixpanelTrack({
                  trackedPageName,
                  type: "Button Click",
                  label: "Open Cube Analytics Tab"
                });
              }
              handleTabIndexChange(newValue as string);
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t("service and support")} value="service-support" />
            <Tab
              label={
                <Badge
                  badgeContent="beta"
                  sx={{ mr: 3 }}
                  slotProps={{
                    badge: {
                      style: {
                        marginRight: -20,
                        color: mainColours.text.secondary,
                        backgroundColor: mainColours.label[5]
                      }
                    }
                  }}
                >
                  {t("Cube Analytics")}
                </Badge>
              }
              value="cube-analytics"
            />
            <Tab label={t("status")} value="status" />
            {shouldRenderTasksTab && <Tab label={t("Tasks")} value="tasks" />}
            {shouldDisplayAndonTab && <Tab label={t("andon")} value="andon" />}
            {shouldDisplayAndonTab && (
              <Tab label={t("events")} value="events" />
            )}
            {shouldRenderConfigurationsTab && (
              <Tab label={t("configurations")} value="configurations" />
            )}
            {enableTestingToolsTab && (
              <Tab label={t("testing tools")} value="testing-tools" />
            )}
          </Tabs>
        )}
      </Box>
      <Box height="100%">
        {tabIndex === "service-support" && <ServiceSupport />}
        {tabIndex === "cube-analytics" && <UnifyView />}
        {tabIndex === "status" && <Status />}
        {tabIndex === "andon" && shouldDisplayAndonTab && <Andon />}
        {tabIndex === "events" && shouldDisplayAndonTab && <EventsTable />}
        {tabIndex === "tasks" && shouldRenderTasksTab && <TaskAssessment />}
        {tabIndex === "configurations" && shouldRenderConfigurationsTab && (
          <Configurations />
        )}
        {tabIndex === "testing-tools" && enableTestingToolsTab && (
          <TestingToolsView />
        )}
      </Box>
    </Stack>
  );
};

export default ServiceSupportView;
